import { z } from 'zod';

export const beastStatusMessageSchema = z.object({
    status: z.string(),
    activity_id: z.number().nullable(),
});

export const videosMessageSchema = z.object({
    swing_uuid: z.string(),
});

export const analysisMessageSchema = z.object({
    swing_uuid: z.string(),
    analysis_url: z.string(),
});

export const imagesMessageSchema = z.object({
    swing_uuid: z.string(),
    urls: z.record(z.string(), z.string()),
});

export const analysisFailedMessageSchema = z.object({
    swing_uuid: z.string(),
});

export const fullAnalysisAvailableMessageSchema = z.object({
    swing_uuid: z.string(),
    activity_id: z.number(),
    swing_id: z.number(),
    analysis_id: z.number(),
});

export const BOOTH_MESSAGE = {
    BEAST_STATUS: 'BEAST_STATUS',
    SWING_VIDEOS_AVAILABLE: 'SWING_VIDEOS_AVAILABLE',
    ANALYSIS_AVAILABLE: 'ANALYSIS_AVAILABLE',
    POSITION_IMAGES_AVAILABLE: 'POSITION_IMAGES_AVAILABLE',
    SWING_ANALYSIS_FAILED: 'SWING_ANALYSIS_FAILED',
    FULL_ANALYSIS_AVAILABLE: 'FULL_ANALYSIS_AVAILABLE',
    LIVE_FEEDBACK_ACTIVE: 'LIVE_FEEDBACK_ACTIVE',
} as const;

export const CLIENT_MESSAGE = {
    KIOSK_STATUS: 'KIOSK_STATUS',
    FLOOR_STATUS: 'FLOORSCREEN_STATUS',
    EVENT_ACK: 'EVENT_ACK',
} as const;

export const beastPayloadSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(BOOTH_MESSAGE.BEAST_STATUS),
        event_id: z.string().optional(),
        content: beastStatusMessageSchema,
    }),
    z.object({
        type: z.literal(BOOTH_MESSAGE.SWING_VIDEOS_AVAILABLE),
        event_id: z.string().optional(),
        content: videosMessageSchema,
    }),
    z.object({
        type: z.literal(BOOTH_MESSAGE.ANALYSIS_AVAILABLE),
        event_id: z.string().optional(),
        content: analysisMessageSchema,
    }),
    z.object({
        type: z.literal(BOOTH_MESSAGE.POSITION_IMAGES_AVAILABLE),
        event_id: z.string().optional(),
        content: imagesMessageSchema,
    }),
    z.object({
        type: z.literal(BOOTH_MESSAGE.SWING_ANALYSIS_FAILED),
        event_id: z.string().optional(),
        content: analysisFailedMessageSchema,
    }),
    z.object({
        type: z.literal(BOOTH_MESSAGE.FULL_ANALYSIS_AVAILABLE),
        event_id: z.string().optional(),
        content: fullAnalysisAvailableMessageSchema,
    }),
]);

export type BeastStatusMessage = z.infer<typeof beastStatusMessageSchema>;
export type VideosMessage = z.infer<typeof videosMessageSchema>;
export type AnalysisMessage = z.infer<typeof analysisMessageSchema>;
export type ImagesMessage = z.infer<typeof imagesMessageSchema>;
export type AnalysisFailedMessage = z.infer<typeof analysisFailedMessageSchema>;
export type FullAnalysisAvailableMessage = z.infer<typeof fullAnalysisAvailableMessageSchema>;

export type BeastMessageContent = {
    [BOOTH_MESSAGE.BEAST_STATUS]: BeastStatusMessage;
    [BOOTH_MESSAGE.SWING_VIDEOS_AVAILABLE]: VideosMessage;
    [BOOTH_MESSAGE.ANALYSIS_AVAILABLE]: AnalysisMessage;
    [BOOTH_MESSAGE.POSITION_IMAGES_AVAILABLE]: ImagesMessage;
    [BOOTH_MESSAGE.SWING_ANALYSIS_FAILED]: AnalysisFailedMessage;
    [BOOTH_MESSAGE.FULL_ANALYSIS_AVAILABLE]: FullAnalysisAvailableMessage;
};

export type BeastPayload = {
    [K in keyof BeastMessageContent]: {
        type: K;
        event_id?: string;
        content: BeastMessageContent[K];
    };
}[keyof BeastMessageContent];

export type KioskStatusMessage = {
    activity_id: number | null;
};

export type FloorStatusMessage = {
    activity_id: number | null;
};

export type ClientMessage =
    | { type: typeof CLIENT_MESSAGE.EVENT_ACK; event_id: string }
    | { type: typeof CLIENT_MESSAGE.KIOSK_STATUS; content: KioskStatusMessage }
    | { type: typeof CLIENT_MESSAGE.FLOOR_STATUS; content: FloorStatusMessage };
